import React, { useCallback, useMemo } from 'react';
import Team from '@mlb/react-patterns/v4/components/Team/team';
import getTeamUrl from '../../utils/getTeamUrl';
import getLeagueLogoId from '../../utils/getLeagueLogoId';
import { DesktopTeamWrapper, MobileTeamWrapper } from './TeamWrappers.style';
import { TeamLogoTypes } from '@mlb/react-patterns/v4/components/TeamLogo/teamLogo.types';
import { getTeamRecordLabel } from 'bdata-sns-shared/utils/getTeamMatchupAriaLabel';

const getUrl = (condensed, id, linkFn, urlRoot) => {
    if (!linkFn) {
        return !condensed ? getTeamUrl(id, urlRoot) : undefined;
    }
    return linkFn.call(this, id, '', urlRoot);
};

const teamsArray = (
    teams = {},
    {
        condensed,
        isMobile,
        alwaysLeftAligned,
        isLoading,
        splitSquadLabel,
        recordBelow,
        customLogoSize,
        customNameFontSize,
        hideRecord,
        DesktopBreakpoint,
        MobileBreakpoint,
        linkFn,
        skeletonNameWidth = 80,
        customTeamName, // the custom prop to use instead of defined rules below,
        teamLogoType = TeamLogoTypes.CAP,
        teamNameTypeDesktop,
        teamNameTypeMobile,
        urlRoot,
        disableTeamPageLink = false,
        dictionary,
        deviceInteraction
    }
) => {
    const handleTeamLink = useCallback((e) => {
        if (typeof deviceInteraction === 'undefined') {
            return false;
        }
        const teamId = e?.currentTarget?.dataset?.teamId;
        const url = e?.currentTarget?.href;

        if (teamId && deviceInteraction?.team({ teamId })) {
            e.preventDefault();
            return false;
        }

        // Android doesn't have a deep link to the team, so try to open in a web view.
        if (url && deviceInteraction?.webview({ url })) {
            e.preventDefault();
            return false;
        }
    }, [deviceInteraction]);

    const handlers = useMemo(() => ({
        onClick: handleTeamLink
    }), [handleTeamLink]);

    // Cannot use Object.values on the teams object coming down because cannot guarantee order
    // sortedTeams will make sure they are properly ordered as we always want away first
    const sortedTeams = [teams.away, teams.home];
    return sortedTeams.map(({ team = {}, leagueRecord = {}, splitSquad, isFavorite } = {}, i) => {
        const { wins, losses } = leagueRecord;
        const { abbreviation, teamName, id, league = {}, placeholder, shortName } = team;
        const teamLabel = getTeamRecordLabel(dictionary, team, leagueRecord);
        const url = disableTeamPageLink ? undefined : getUrl(condensed, id, linkFn, urlRoot);
        const linkProps = url ? {
            handlers,
            url,
            ariaLabel: teamLabel,
            dataAttr: {
                'data-team-name': teamName,
                'data-team-id': id
            }
        } : undefined;
        const leftAligned = alwaysLeftAligned || i === 0;

        const leagueId = league.id;
        const desktopTeamName = customTeamName ? team[customTeamName] : team[teamNameTypeDesktop] || teamName;
        const mobileTeamName = customTeamName ? team[customTeamName] : team[teamNameTypeMobile] || abbreviation;
        const TeamLabels = (
            <React.Fragment>
                <DesktopTeamWrapper breakpoint={ DesktopBreakpoint } aria-hidden='true' >{ desktopTeamName }</DesktopTeamWrapper>
                <MobileTeamWrapper breakpoint={ MobileBreakpoint } aria-hidden='true'>{ mobileTeamName }</MobileTeamWrapper>
            </React.Fragment>
        );

        return (
            <Team
                leftAligned={ leftAligned }
                recordBelow={ typeof recordBelow !== 'undefined' ? recordBelow : isMobile }
                displayRecord={ !hideRecord }
                // design for preview condensed shows expanded, but ticket wants tricode https://baseball.atlassian.net/browse/BDFED-3303
                name={ TeamLabels }
                wins={ wins }
                losses={ losses }
                teamId={ placeholder && leagueId ? getLeagueLogoId(leagueId) : id }
                key={ id }
                linkProps={ linkProps }
                isLoading={ isLoading }
                splitSquad={ splitSquad ? splitSquadLabel : '' }
                isMobile={ isMobile }
                logoSize={ customLogoSize }
                nameFontSize={ customNameFontSize }
                skeletonNameWidth={ (hideRecord && !isMobile) ? skeletonNameWidth : undefined}
                dataAttr={{ 'data-mlb-test': 'teamLogos' }}
                isFavorite={ isFavorite }
                teamLogoType = { teamLogoType }
                alt={desktopTeamName}
                ariaLabel={teamLabel}
                fullTeamName={shortName}
                ariaHidden={condensed}
            />
        );
    });
};

export default teamsArray;
